import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  FormField,
  FormSelect,
  FormTextarea,
  SkeletonCard,
} from "@/components/ui";
import { getErrorMessage, isApiError } from "@/api/client";
import { centsToUsd } from "@/lib/money";
import {
  PARTY_ADDON_LABELS,
  PARTY_FORMAT_LABELS,
  PARTY_OCCASION_LABELS,
  PARTY_SELECTION_COUNT_BY_FORMAT,
  type PartyFormat,
  type PartyOccasion,
} from "@/api/types/party";
import { basePriceCentsPreview, isWeekendIso, totalPriceCentsPreviewWithSelections } from "@/features/parties/partyMath";
import {
  usePublicEventRooms,
  usePublicEventRoomSlots,
  usePublicPartyActivities,
  useSubmitPartyInquiry,
  type PublicPartyInquiryResponse,
} from "./usePublicParties";

const PARTY_INCLUDED_ITEMS = ["Tables & chairs", "Art your way", "Party host", "Water station"];

const BUSINESS_NAME = "The Fox & The Owl";
const RATE_LIMIT_MESSAGE = "Too many attempts — please try again in a minute.";

/**
 * PUBLIC party inquiry page (Phase 2d) — reached at /parties/inquire, no
 * auth, no AppShell, same tier as /register-classes
 * (features/portal/ClassRegistrationPage.tsx, which this page's overall
 * shape mirrors: a single continuous form, occasion survey up front
 * driving a post-submit suggested-add-ons list, a honeypot field). $350
 * for a 2-hour weekend kids party (art supplies + host included) — the
 * occasion survey (occasion, headcount, child's age) is what drives
 * app/services/party_pricing.py::suggest_addons.
 */
export function PartyInquiryPage() {
  const { data: rooms, isLoading: roomsLoading, error: roomsError } = usePublicEventRooms();
  const { data: slots } = usePublicEventRoomSlots();
  const submitInquiry = useSubmitPartyInquiry();

  const [resourceId, setResourceId] = useState("");
  const [slotId, setSlotId] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [extraHour, setExtraHour] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [occasion, setOccasion] = useState<PartyOccasion>("birthday");
  const [childAge, setChildAge] = useState("");
  const [notes, setNotes] = useState("");
  const [emailConsent, setEmailConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot

  // Zone A: format is OPTIONAL on the public inquiry (unlike the admin
  // create form) — "" means "haven't decided yet."
  const [format, setFormat] = useState<PartyFormat | "">("");
  const [selections, setSelections] = useState<string[]>([]);

  const [result, setResult] = useState<PublicPartyInquiryResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!resourceId && rooms && rooms.length > 0) setResourceId(rooms[0].id);
  }, [rooms, resourceId]);

  useEffect(() => {
    setSelections([]);
  }, [format]);

  const { data: activities } = usePublicPartyActivities();
  const catalogForFormat = useMemo(
    () => (format ? (activities ?? []).filter((a) => a.format === format) : []),
    [activities, format],
  );
  const selectedItems = useMemo(
    () => catalogForFormat.filter((a) => selections.includes(a.id)),
    [catalogForFormat, selections],
  );
  const requiredCount = format ? PARTY_SELECTION_COUNT_BY_FORMAT[format] : 0;

  function toggleSelection(id: string) {
    setSelections((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= requiredCount) return prev;
      return [...prev, id];
    });
  }

  const isWeekend = bookingDate ? isWeekendIso(bookingDate) : true;
  const priceCents = useMemo(
    () => (format ? totalPriceCentsPreviewWithSelections(extraHour, [], selectedItems, headcount.trim() ? Number(headcount) : null) : basePriceCentsPreview(extraHour)),
    [format, extraHour, selectedItems, headcount],
  );

  const canSubmit =
    !!resourceId &&
    !!slotId &&
    !!bookingDate &&
    isWeekend &&
    !!name.trim() &&
    (!format || selections.length === requiredCount) &&
    !submitInquiry.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      const response = await submitInquiry.mutateAsync({
        resource_id: resourceId,
        slot_id: slotId,
        booking_date: bookingDate,
        extra_hour: extraHour,
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        headcount: headcount.trim() ? Number(headcount) : null,
        occasion,
        child_age: childAge.trim() ? Number(childAge) : null,
        notes: notes.trim() || undefined,
        email_consent: emailConsent,
        sms_consent: smsConsent,
        website,
        format: format || undefined,
        selections: format ? selections : undefined,
      });
      setResult(response);
    } catch (err) {
      if (isApiError(err) && err.response?.status === 429) {
        setSubmitError(RATE_LIMIT_MESSAGE);
      } else {
        setSubmitError(getErrorMessage(err));
      }
    }
  }

  return (
    <div className="min-h-screen bg-bg-body px-4 py-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-wide text-copper">{BUSINESS_NAME}</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-text-primary sm:text-3xl">Book a kids party</h1>
          <p className="mt-2 text-sm text-text-secondary">
            $350 for a 3-hour weekend party. +$75 for an extra hour.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Every party includes: {PARTY_INCLUDED_ITEMS.join(", ")}.
          </p>
        </header>

        {result ? (
          <PartyInquiryConfirmation result={result} onInquireAgain={() => setResult(null)} />
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} noValidate className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>1. Pick a date &amp; room</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {roomsLoading ? (
                  <SkeletonCard />
                ) : roomsError ? (
                  <ApiError error={roomsError} title="Unable to load rooms" />
                ) : (
                  <>
                    <FormSelect label="Room" value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
                      {(rooms ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </FormSelect>
                    <FormField
                      label="Date (weekends only)"
                      type="date"
                      required
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      data-testid="party-inquiry-date"
                    />
                    {bookingDate && !isWeekend && (
                      <p className="text-sm text-danger" role="alert">
                        Parties are a weekend-only product — pick a Saturday or Sunday.
                      </p>
                    )}
                    <FormSelect label="Time slot" value={slotId} onChange={(e) => setSlotId(e.target.value)}>
                      <option value="">Select a slot…</option>
                      {(slots ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </FormSelect>
                    <Checkbox
                      label="Add an adjacent hour (+$75)"
                      checked={extraHour}
                      onChange={(e) => setExtraHour(e.target.checked)}
                    />
                    <p className="text-sm text-text-secondary" data-testid="party-inquiry-price-preview">
                      Price preview: <span className="font-semibold text-text-primary">{centsToUsd(priceCents)}</span>
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>2. Pick a format (optional)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormSelect
                  label="Format — decide now or later"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as PartyFormat | "")}
                >
                  <option value="">I'll decide later</option>
                  {Object.entries(PARTY_FORMAT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </FormSelect>

                {format && (
                  <>
                    <p className="text-sm text-text-secondary" data-testid="party-inquiry-selection-counter">
                      {selections.length} of {requiredCount} selected
                    </p>
                    <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                      {catalogForFormat.map((item) => {
                        const checked = selections.includes(item.id);
                        const disabled = !checked && selections.length >= requiredCount;
                        return (
                          <Checkbox
                            key={item.id}
                            label={item.name}
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleSelection(item.id)}
                            data-testid={`party-inquiry-catalog-item-${item.id}`}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>3. Tell us about the party</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormSelect label="Occasion" value={occasion} onChange={(e) => setOccasion(e.target.value as PartyOccasion)}>
                  {Object.entries(PARTY_OCCASION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </FormSelect>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    label="Headcount (optional)"
                    type="number"
                    min={0}
                    value={headcount}
                    onChange={(e) => setHeadcount(e.target.value)}
                  />
                  <FormField
                    label="Birthday child's age (optional)"
                    type="number"
                    min={0}
                    max={18}
                    value={childAge}
                    onChange={(e) => setChildAge(e.target.value)}
                  />
                </div>
                <FormTextarea label="Anything we should know? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>4. Your contact info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="Your name" required value={name} onChange={(e) => setName(e.target.value)} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  <FormField label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <Checkbox label="OK to email me" checked={emailConsent} onChange={(e) => setEmailConsent(e.target.checked)} />
                <Checkbox label="OK to text me" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />
              </CardContent>
            </Card>

            {/* Honeypot — invisible to a real guardian, tabIndex -1 so it's
                never reached by keyboard navigation, but present in the DOM
                for a bot that auto-fills every field to trip. */}
            <div className="sr-only" aria-hidden="true">
              <label htmlFor="party-inquiry-website">Website</label>
              <input
                id="party-inquiry-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {submitError && (
              <p className="text-center text-sm text-danger" role="alert" data-testid="party-inquiry-error">
                {submitError}
              </p>
            )}

            <Button type="submit" size="lg" disabled={!canSubmit} data-testid="submit-party-inquiry">
              {submitInquiry.isPending ? "Submitting…" : "Send inquiry"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function PartyInquiryConfirmation({
  result,
  onInquireAgain,
}: {
  result: PublicPartyInquiryResponse;
  onInquireAgain: () => void;
}) {
  return (
    <Card className="text-center" data-testid="party-inquiry-confirmation">
      <CardContent className="flex flex-col items-center gap-3 py-8">
        <span className="text-4xl" role="img" aria-label="Party">
          🎉
        </span>
        <p className="text-lg font-semibold text-text-primary">Thanks! We've got your inquiry.</p>
        <p className="max-w-sm text-sm text-text-secondary">
          The Fox &amp; The Owl will reach out to confirm your party date.
        </p>
        {result.suggested_addons.length > 0 && (
          <div className="mt-2">
            <p className="text-sm font-semibold text-text-primary">You might also like:</p>
            <p className="text-sm text-text-secondary" data-testid="party-inquiry-suggestions">
              {result.suggested_addons.map((a) => PARTY_ADDON_LABELS[a]).join(" · ")}
            </p>
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={onInquireAgain} className="mt-2">
          Send another inquiry
        </Button>
      </CardContent>
    </Card>
  );
}
